// Unread state for the top-bar Chats glyph and the Chats list rows.
//
// Why this exists: the API's only unread signals are per-account
// (profile.message_count, clear-badge) or per-thread counters that do not
// reliably drop to zero the instant a thread is opened. The spec needs two
// things the server can't give us on its own — a dot that "appears and
// disappears after being seen", and a row dot that clears the moment its thread
// is opened — so we layer a local read watermark per thread over the server
// counts.
//
// One predicate (`isThreadUnread`) feeds BOTH the row dot and the header dot, so
// the two can never disagree. That is the whole point: unread means the same
// thing everywhere.

import { toDate } from "./timeFormat";

const THREAD_KEY = "chatThreadsSeenAt";

function readThreadMap() {
  try {
    const parsed = JSON.parse(localStorage.getItem(THREAD_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

export function getThreadSeenAt(threadId) {
  if (threadId == null) return 0;
  const n = parseInt(readThreadMap()[String(threadId)], 10);
  return Number.isNaN(n) ? 0 : n;
}

// Called when a thread is opened (and again on each message received while it is
// open). Only threads the user has actually visited are stored, so the map stays
// small.
//
// Pass the NEWEST MESSAGE'S OWN timestamp as `when`, not wall-clock time: the
// comparison in isThreadUnread is then timestamp-vs-timestamp in one frame of
// reference, so it can't be skewed if the API's date strings and the browser
// clock disagree about timezone. Wall-clock is only the last resort.
export function markThreadSeen(threadId, when = Date.now()) {
  if (threadId == null) return;
  const map = readThreadMap();
  map[String(threadId)] = when;
  try {
    localStorage.setItem(THREAD_KEY, JSON.stringify(map));
  } catch (e) {
    // Storage full / disabled — unread falls back to the raw server count.
  }
}

function lastMessageTime(chat) {
  const d = toDate(chat?.last_message?.created_at);
  return d ? d.getTime() : 0;
}

// A thread is unread when the last message is somebody else's, the server still
// flags it as unseen, and it arrived after we last opened that thread.
export function isThreadUnread(chat, myId) {
  const last = chat?.last_message;
  if (!last) return false;

  const sender = last.user_id;
  if (myId != null && sender != null && String(sender) === String(myId)) {
    return false;
  }

  const counted = parseInt(chat.message_count, 10) > 0;
  const seen = last.is_seen;
  const flaggedUnseen =
    seen != null && (seen === 0 || seen === "0" || seen === false);
  if (!counted && !flaggedUnseen) return false;

  const at = lastMessageTime(chat);
  if (!at) return true; // No timestamp to compare — trust the server flag.
  return at > getThreadSeenAt(chat.id);
}

export function countUnreadThreads(threads, myId) {
  return (threads || []).filter((c) => isThreadUnread(c, myId)).length;
}

// Broadcast to the header. The event name/shape is pre-existing chrome that
// DashboardHeader already listens on; this keeps every caller honest about
// writing localStorage and firing the event together.
export function publishBadgeCounts({ messageCount, notificationCount }) {
  const next = {
    messageCount:
      messageCount != null
        ? messageCount
        : localStorage.getItem("messageCount") || 0,
    notificationCount:
      notificationCount != null
        ? notificationCount
        : localStorage.getItem("notificationCount") || 0,
  };
  localStorage.setItem("messageCount", next.messageCount);
  localStorage.setItem("notificationCount", next.notificationCount);
  window.dispatchEvent(new CustomEvent("myCustomEvent", { detail: next }));
}
